import React, { useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { DashboardApi } from '../services/api';
import './ChangelogPage.css';

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('no-NO', { year: 'numeric', month: 'long', day: 'numeric' });
};

const renderMarkdown = (value) => {
  if (!value) return '';
  try {
    return DOMPurify.sanitize(marked.parse(String(value)));
  } catch (_error) {
    return '';
  }
};

const CATEGORY_CLASS = {
  'Ny funksjon': 'feature',
  Forbedring: 'improvement',
  Feilretting: 'fix',
  Annet: 'other',
};

const ChangelogPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      try {
        const data = await DashboardApi.getChangelog();
        const list = Array.isArray(data?.entries) ? data.entries : [];
        if (!isMounted) return;
        setEntries(list);
        setError(null);

        const unseenIds = list.filter((entry) => !entry.seen).map((entry) => entry.id);
        if (unseenIds.length > 0) {
          DashboardApi.markChangelogSeenBulk(unseenIds).catch(() => {});
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || 'Klarte ikke hente endringsloggen.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const renderedEntries = useMemo(
    () => entries.map((entry) => ({
      ...entry,
      html: renderMarkdown(entry.content),
    })),
    [entries],
  );

  return (
    <div className="page changelog-page">
      <div className="page-header">
        <div>
          <h1>Endringslogg</h1>
          <p className="page-subtitle">
            Nyheter, forbedringer og feilrettinger i KI Chat.
          </p>
        </div>
      </div>

      {error && (
        <div className="card changelog-error">
          <p>⚠️ {error}</p>
        </div>
      )}

      {loading ? (
        <div className="card changelog-empty">
          <p>Laster endringslogg…</p>
        </div>
      ) : renderedEntries.length === 0 ? (
        <div className="card changelog-empty">
          <p>Ingen endringslogg-innlegg ennå.</p>
        </div>
      ) : (
        <div className="changelog-list">
          {renderedEntries.map((entry) => (
            <article key={entry.id} className="card changelog-entry">
              <header className="changelog-entry-header">
                <div className="changelog-entry-heading">
                  <h2 className="changelog-entry-title">{entry.title}</h2>
                  {entry.summary && (
                    <p className="changelog-entry-summary">{entry.summary}</p>
                  )}
                </div>
                <div className="changelog-entry-meta">
                  {entry.version && (
                    <span className="changelog-version">v{entry.version.replace(/^v/i, '')}</span>
                  )}
                  {entry.category && (
                    <span className={`changelog-category ${CATEGORY_CLASS[entry.category] || 'other'}`}>
                      {entry.category}
                    </span>
                  )}
                  <time className="changelog-date">
                    {formatDate(entry.publishedAt || entry.createdAt)}
                  </time>
                </div>
              </header>
              {entry.html && (
                <div
                  className="changelog-content"
                  dangerouslySetInnerHTML={{ __html: entry.html }}
                />
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChangelogPage;
